import { matchPath } from "react-router-dom";

import { routers } from "@/configs/routers";

import { hasCookieConsent } from "./cookieConsent";
import { safeLocation } from "./gtag";

/** Product events; props carry numbers and short strings only, never ids of people. */
export type AnalyticsEvent =
  | "sport_slip_open"
  | "sport_pick_added"
  | "sport_bet_placed"
  | "sport_quick_bet"
  | "sport_cashout"
  // GA4 recommended names, so they fill GA's built-in reports.
  | "sign_up"
  | "login"
  | "search"
  | "logout"
  | "game_open"
  | "game_favorite"
  | "deposit_start"
  | "withdraw_request"
  | "bonus_claim"
  | "rakeback_claim"
  | "vip_boost_claim"
  | "daily_checkin"
  | "mission_claim"
  | "daily_spin"
  | "tournament_join";

type Props = Record<string, string | number | boolean | undefined>;

/**
 * One place every product event goes through. With analytics consent it lands on
 * `window.dataLayer` (what a tag manager reads), as a GA4 event when the Google tag is
 * loaded (lib/gtag.ts), and on a DOM event any script can listen to; without consent
 * nothing leaves the page.
 */
export const track = (event: AnalyticsEvent, props: Props = {}): void => {
  if (typeof window === "undefined") return;
  const payload = { event, ...props, ts: Date.now() };
  if (process.env.NODE_ENV !== "production") console.debug("[track]", payload);
  if (!hasCookieConsent("analytics")) return;
  (window.dataLayer ??= []).push(payload);
  window.gtag?.("event", event, props);
  window.dispatchEvent(new CustomEvent("sky:track", { detail: payload }));
};

// "/casino/<original>" paths, table routes folded in; every other /casino/:code is a provider lobby.
const ORIGINAL_PATHS = new Set(
  Object.values(routers)
    .filter((path) => path.startsWith("/casino/") && path !== routers.CasinoGame)
    .filter((path) => path !== routers.MyBetsCasino)
    .map((path) => path.split("/").slice(0, 3).join("/")),
);

// First path segments the site has; anything else (bots, typos) is "other", not a new event name.
const KNOWN_ROOTS = new Set(Object.values(routers).map((path) => path.split("/")[1]));

const ACCOUNT = new Set(["settings", "transactions", "notifications"]);
const LEGAL = new Set([
  "privacy-policy",
  "terms-service",
  "responsible-gambling",
  "aml-kyc-policy",
  "bonus-terms",
  "cookie-policy",
  "about-us",
]);

/** The part of the site a path belongs to, sent as GA4's content group. */
export const pageSection = (pathname: string): string => {
  const [first = "", second = ""] = pathname.split("/").filter(Boolean);
  if (!first) return "home";
  if (second === "my-bets") return "my_bets";
  if (first === "casino") {
    return ORIGINAL_PATHS.has(`/casino/${second}`) ? "originals" : "casino";
  }
  if (first === "games" || first === "recent" || first === "favorites") return "casino";
  if (first === "game-room") return "game_play";
  if (ACCOUNT.has(first)) return "account";
  if (LEGAL.has(first)) return "legal";
  if (!KNOWN_ROOTS.has(first)) return "other";
  return first.replace(/-/g, "_");
};

// Static sportsbook paths first: matchPath would read "/sports/in-play" as a sport slug otherwise.
const SPORT_PAGES: [string, string][] = [
  [routers.SportsLobby, "lobby"],
  [routers.SportsHome, "home"],
  [routers.SportsInPlay, "in_play"],
  [routers.SportsNext24, "next_24h"],
  [routers.SportsUpcoming, "upcoming"],
  [routers.SportsFavorites, "favourites"],
  [routers.SportsPromotions, "promotions"],
  [routers.SportsSearch, "search"],
  [routers.MyBetsSport, "my_bets"],
  [routers.SportRegion, "region"],
  [routers.SportLeague, "league"],
  [routers.Sports, "sport"],
  [routers.SportEvent, "event"],
];

/** What a page view says beyond its path: the sportsbook page and its sport, the game opened. */
const pageDetail = (pathname: string, section: string): Props => {
  if (section === "sports" || pathname === routers.MyBetsSport) {
    for (const [pattern, page] of SPORT_PAGES) {
      const match = matchPath(pattern, pathname);
      if (!match) continue;
      const { sportSlug, regionSlug, leagueSlug, eventId, eventStatus } = match.params;
      return {
        sport_page: page,
        sport: sportSlug,
        region: regionSlug,
        league: leagueSlug,
        match_id: eventId,
        // live, next24, upcoming or competitions on a sport's list; live or prematch on a match.
        status: eventStatus,
      };
    }
    return { sport_page: "other" };
  }
  const [first, second, third] = pathname.split("/").filter(Boolean);
  if (section === "originals") return { game: second };
  if (first === "games") return { provider: second, game_id: third };
  if (section === "casino" && first === "casino") return { category: second };
  if (section === "game_play") return { game_id: second };
  if (first === "tournaments" && second) return { tournament_id: second };
  return {};
};

let pendingPageView: ReturnType<typeof setTimeout> | undefined;

/**
 * A GA4 page_view for an in-app navigation, plus a `view_<section>` event (view_sports,
 * view_casino, ...) so each part of the site is its own event in GA. Not consent-gated like
 * track(): Consent Mode sends them cookieless until analytics is accepted. Waits a moment for
 * the page's own title.
 */
export const trackPageView = (pathname: string): void => {
  if (!window.gtag) return;
  clearTimeout(pendingPageView);
  pendingPageView = setTimeout(() => {
    const section = pageSection(pathname);
    const detail = pageDetail(pathname, section);
    const pageLocation = safeLocation();
    if (process.env.NODE_ENV !== "production") console.debug("[page_view]", section, detail);
    // Every later event of this page (scroll, engagement, track()) reports the cleaned URL too.
    window.gtag?.("set", { page_location: pageLocation });
    window.gtag?.("event", "page_view", {
      page_location: pageLocation,
      page_path: pathname,
      page_title: document.title,
      content_group: section,
      ...detail,
    });
    window.gtag?.("event", `view_${section}`, { page_path: pathname, ...detail });
  }, 300);
};
